/* eslint-disable no-param-reassign */
import { useCallback, useEffect, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button, Divider, Grid, Typography, Paper, Stack, Link, Box } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import SendIcon from '@mui/icons-material/Send';
import HelpCenter from '@mui/icons-material/HelpCenter';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import ControlledTextEditor from 'src/shared/components/form/controlledTextEditor';
import { FormErrorSummary } from 'src/shared/components/form/formErrorSummary';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { useGlobals } from 'src/shared/contexts/appContext';
import { SelectionForm } from 'src/modules/groupMessageEmail/components/selectionForm';
import { GroupMessageRequestPost } from 'src/modules/groupMessageEmail/models/groupMessageRequestPost';
import { createGroupMessage, getGroupMessageEmailTemplateById, getGroupMessageEmailTemplateByName } from 'src/shared/services/groupMessageService';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { ControlledFileDropZone } from 'src/shared/components/form/fileDropZone';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { removeBase64Images } from 'src/shared/functions/removeBase64Images';
import { getSenderTitle } from 'src/shared/functions/getSenderTitle';
import { AddCircle, Pageview } from '@mui/icons-material';
import { isFeatureEnabled } from 'src/shared/functions/isFeatureEnabled';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import { SelectionTypeEnum } from '../models/selectionType.enum';
import { RemovedImagesDialog } from '../components/removedImagesDialog';
import { FixedSelection } from '../models/fixedSelection';
import { Selection } from '../models/selection';
import { useSchemaCreateGroupMessageEmail } from '../schemas/useSchemaCreateGroupMessageEmail';
import { GroupMessageEmailPreviewModal } from '../components/groupMessageEmailPreviewModal';
import { GroupMessageEmailTemplateCreateModal } from '../components/groupMessageEmailTemplateCreateModal';

const GroupMessageEmailCreatePage = () => {
  const params = useParams<{ id: string }>();
  const templateId = Number(params.id);
  const { currentUser } = useGlobals();
  const { t } = useTranslation(['groupMessage', 'common']);
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(false);
  const [showPreviewModal, setShowPreviewModal] = useState<boolean>(false);
  const [sendMode, setSendMode] = useState<boolean>(false);
  const [openCreateEmailTemplateModal, setOpenCreateEmailTemplateModal] = useState<boolean>(false);
  const [removedImages, setRemovedImages] = useState<string[]>([]);
  const [dataToSave, setDataToSave] = useState<GroupMessageRequestPost>();
  const [, setFormErrors] = useState<any>();

  const [selection, setSelection] = useState<Selection | null>(null);
  const [fixedSelection, setFixedSelection] = useState<FixedSelection | null>(null);

  // const [files, setFiles] = useState<File[]>([]);
  // const onFilesChange = (newFiles: File[]) => {
  //   setFiles(newFiles);
  // };

  const isMounted = useIsMounted();

  const createGroupMessageEmailSchema = useSchemaCreateGroupMessageEmail();
  type CreateGroupMessageFormInterface = yup.InferType<typeof createGroupMessageEmailSchema>;

  const formMethods = useForm<CreateGroupMessageFormInterface>({
    resolver: yupResolver(createGroupMessageEmailSchema),
    mode: 'all',
    defaultValues: {
      subject: '',
      sender: currentUser?.email || '',
      speedFormValues: '',
      speedFormText: '',
      senderTitle: getSenderTitle(currentUser),
      selectionType: SelectionTypeEnum.Selection,
      circularName: null,
      body: '',
      recipients: '',
      selection_id: 0,
      files: [],
      isHTML: true,
      send: false,
      excludedMemberFromSelection: [],
    },
  });

  const {
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting, isValid },
    formState,
    trigger,
  } = formMethods;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let res;
      if (!templateId) {
        res = await getGroupMessageEmailTemplateByName(params.id ?? '');
      } else {
        res = await getGroupMessageEmailTemplateById(templateId || 0);
      }
      if (isMounted()) {
        if (res) {
          setValue('body', res.data);
        }
      }
    } catch (e) {
      ConsoleHelper.log(e);
    } finally {
      setLoading(false);
    }
  }, [isMounted, params.id, setValue, templateId]);

  useEffect(() => {
    if (isMounted()) {
      fetchData();
    }
  }, [fetchData, isMounted]);

  const postData = useCallback(
    async (data) => {
      ConsoleHelper.log('postData', data);
      try {
        data.send = sendMode;
        data.circularName = data.subject;
        const res = await createGroupMessage(data);
        ConsoleHelper.log('res: ', res);
        if (isMounted()) {
          if (sendMode) toast.success(<>{t('groupMessage:create.sendSuccess')}</>);
          else toast.success(<>{t('groupMessage:create.saveSuccess')}</>);
          navigate(RoutesEnum.GroupMessageEmailOverview);
        }
      } catch (e) {
        if (isMounted()) {
          if (data.send) toast.warning(<>{t('groupMessage:create.sendError')}</>);
          else toast.warning(<>{t('groupMessage:create.saveError')}</>);
          ConsoleHelper.error('saveFormDataError:', e);
        }
      }
    },
    [isMounted, navigate, sendMode, t],
  );

  const saveFormData = useCallback(
    async (data: GroupMessageRequestPost) => {
      ConsoleHelper.log('saveFormData', data);
      const { modifiedContent, strippedImages } = removeBase64Images(data.body);
      // Spara arrayen med bilder i statet
      setRemovedImages(strippedImages);
      if (strippedImages.length > 0) {
        data.body = modifiedContent;
        ConsoleHelper.log('modifiedContent', modifiedContent);
        setValue('body', modifiedContent);
        setDataToSave(data);
      } else {
        postData(data);
      }
    },
    [postData, setValue],
  );

  useEffect(() => {
    if (formState.errors) {
      setFormErrors(formState.errors);
    }
  }, [formState]);

  const onSubmit = async (data: CreateGroupMessageFormInterface) => {
    const { excludedMemberFromSelection, ...rest } = data;

    const dataToPost: GroupMessageRequestPost = {
      ...rest,
      includeRecipientsThatHasUnsubscribedFromEmail: false,
      excludeMemberIds: excludedMemberFromSelection?.map((member) => member.member_id).filter((id): id is number => id !== undefined) || [],
    };

    ConsoleHelper.log('dataToPost', dataToPost);

    await trigger();
    await saveFormData(dataToPost);
  };

  const textEditorInitalized = useCallback(() => {
    return null;
  }, []);

  const handleConfirmImageRemoval = () => {
    postData(dataToSave);
  };

  const returnToOverview = () => {
    navigate(RoutesEnum.GroupMessageEmailOverview);
  };

  return (
    <>
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <GridWrapper fixed>
            <LoadingOverlay loading={loading} />
            <Grid item xs={12} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', marginBottom: -5 }}>
              <MainContentHeader title={t('groupMessage:create.header')} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button variant='outlined' startIcon={<AddCircle />} onClick={() => setOpenCreateEmailTemplateModal(true)} disableElevation>
                  {t('groupMessage:create.createNewTemplate')}
                </Button>
                <Link
                  href='https://hjalp.foreningssupport.se/article/312-kommunikation-e-postutskick#Skapa-ett-nytt-e-postutskick-mWATY'
                  target='_blank'
                  rel='noopener'
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                >
                  <HelpCenter fontSize='small' />
                  {t('groupMessage:create.helpLinkText')}
                </Link>
              </Box>
            </Grid>
            {removedImages && removedImages.length > 0 && (
              <RemovedImagesDialog
                removedImages={removedImages}
                open
                handleClose={() => setRemovedImages([])}
                confirmImageRemoval={handleConfirmImageRemoval}
              />
            )}
            <GroupMessageEmailPreviewModal
              modalOpen={showPreviewModal}
              onClose={() => setShowPreviewModal(false)}
              onSave={() => {
                setShowPreviewModal(false);
                returnToOverview();
              }}
            />
            <Grid item xs={12}>
              <Paper
                sx={{
                  p: 3,
                }}
              >
                <GridWrapper>
                  <Grid item xs={12} lg={8}>
                    <GridWrapper useBreakpoints={false}>
                      <Grid item xs={12}>
                        <SelectionForm
                          selectionFromServer={null}
                          fixedSelectionFromServer={null}
                          onSelection={(v) => setSelection(v)}
                          onFixedSelection={(v) => setFixedSelection(v)}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <ControlledTextField
                          name='subject'
                          required
                          label={t('groupMessage:formFields.subject')}
                          inputProps={{ maxLength: 50 }} // TODO
                          InputLabelProps={{
                            shrink: true,
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <ControlledTextEditor name='body' initCallback={textEditorInitalized} resizeAllowed rows={50} />
                      </Grid>
                      <Grid item xs={12}>
                        <ControlledTextField
                          name='sender'
                          required
                          label={t('groupMessage:formFields.sender')}
                          InputLabelProps={{
                            shrink: true,
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <ControlledTextField
                          name='senderTitle'
                          required
                          label={t('groupMessage:formFields.senderTitle')}
                          InputLabelProps={{
                            shrink: true,
                          }}
                        />
                      </Grid>{' '}
                      <Grid item xs={12}>
                        <ControlledTextField
                          name='recipients'
                          label={t('groupMessage:formFields.recipients')}
                          helperText={t('groupMessage:formFields.recipients_helperText')}
                          InputLabelProps={{
                            shrink: true,
                          }}
                        />
                      </Grid>
                    </GridWrapper>
                  </Grid>
                  <Grid item xs={12} lg={4}>
                    <GridWrapper useBreakpoints={false}>
                      <Grid item xs={12}>
                        <ControlledFileDropZone
                          name='files'
                          showFileHeader
                          disabled={false}
                          documentFiles={undefined}
                          orientation='vertical'
                          multiple
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormErrorSummary translationNamespace='groupMessage' errors={errors} />
                      </Grid>
                    </GridWrapper>
                  </Grid>
                  <Grid item xs={12} textAlign='right' sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Stack direction='row' spacing={1}>
                      <Button variant='outlined' type='button' onClick={returnToOverview}>
                        {t('common:button.cancel')}
                      </Button>
                      {isFeatureEnabled() && (
                        <Button
                          variant='outlined'
                          type='button'
                          disabled={Object.keys(errors).length > 0}
                          onClick={async () => {
                            await trigger();
                            if (isValid) setShowPreviewModal(true);
                          }}
                          startIcon={<Pageview aria-hidden />}
                        >
                          {t('common:button.preview')}
                        </Button>
                      )}
                      <LoadingButton
                        loading={isSubmitting}
                        variant='contained'
                        type='submit'
                        disableElevation
                        onClick={() => setSendMode(false)}
                        disabled={Object.keys(errors).length > 0 && !isValid}
                      >
                        {t('common:button.save')}
                      </LoadingButton>
                      <Divider orientation='vertical' variant='middle' flexItem />
                      <LoadingButton
                        loading={isSubmitting}
                        variant='contained'
                        type='submit'
                        disableElevation
                        disabled={Object.keys(errors).length > 0 || (!selection && !fixedSelection)}
                        onClick={() => setSendMode(true)}
                        endIcon={<SendIcon aria-hidden />}
                      >
                        {t('common:button.saveAndSend')}
                      </LoadingButton>
                    </Stack>
                  </Grid>
                </GridWrapper>
              </Paper>
            </Grid>
          </GridWrapper>
        </form>
      </FormProvider>
      <GroupMessageEmailTemplateCreateModal body={watch('body')} open={openCreateEmailTemplateModal} openModal={setOpenCreateEmailTemplateModal} />
    </>
  );
};

export default GroupMessageEmailCreatePage;
